import { useState } from "react";
import { Link } from "react-router-dom";
import { formatAssetLabel, getAssetIcon } from "../../constants/platformAssets";
import { formatHashtag } from "../../utils/campaignPayload";
import { buildCampaignPath } from "../../constants/appRoutes";
import "bootstrap-icons/font/bootstrap-icons.css";

function isNewMediaFormat(media) {
    return Boolean(media?.suggestion ?? media?.ai_prompt);
}

function getMediaTypeLabel(type) {
    if (type === "video") return "Video";
    if (type === "image") return "Image";
    if (!type) return null;
    return formatAssetLabel(type);
}

export function getPostMediaList(media) {
    if (!media) return [];
    if (Array.isArray(media)) return media.filter(Boolean);
    return [media];
}

export function hasPostMedia(post) {
    return getPostMediaList(post?.media).length > 0;
}

function formatSingleMediaCopyText(media) {
    if (!media) return "";

    if (isNewMediaFormat(media)) {
        const lines = [];
        const typeLabel = getMediaTypeLabel(media.type);
        if (typeLabel) lines.push(`Type: ${typeLabel}`);
        if (media.suggestion) lines.push(`Suggestion: ${media.suggestion}`);
        if (media.ai_prompt) lines.push(`AI prompt: ${media.ai_prompt}`);
        return lines.join("\n");
    }

    const { type, description = {} } = media;
    const lines = [];
    if (type) lines.push(`Type: ${type}`);
    if (description.image) lines.push(`Image: ${description.image}`);
    if (description.video_storyboard) lines.push(description.video_storyboard);
    if (description.voiceover_or_song) {
        lines.push(`Voiceover/Song: ${description.voiceover_or_song}`);
    }
    if (description.tips?.length) {
        lines.push(...description.tips.map((tip) => `- ${tip}`));
    }
    return lines.join("\n");
}

export function formatMediaCopyText(media) {
    const items = getPostMediaList(media);
    if (items.length === 0) return "";

    return items
        .map((item, index) => {
            const text = formatSingleMediaCopyText(item);
            return items.length > 1 ? `[${index + 1}] ${text}` : text;
        })
        .join("\n\n");
}

/** @deprecated use PostTypeHeader for post cards */
export function PostAssetBadge({ assetType, className = "" }) {
    if (!assetType) return null;

    const icon = getAssetIcon(assetType);

    return (
        <span
            className={`inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700 ${className}`}
        >
            <i className={`bi ${icon} text-[10px]`} aria-hidden />
            {formatAssetLabel(assetType)}
        </span>
    );
}

export function PostTypeHeader({
    assetType,
    createdLabel = null,
    campaignId = null,
    className = "",
}) {
    const resolvedAssetType = assetType || DEFAULT_POST_ASSET_TYPE;
    const icon = getAssetIcon(resolvedAssetType);
    const hasSideMeta = Boolean(createdLabel || campaignId);

    return (
        <div
            className={`flex items-center justify-between gap-3 border-l-4 border-l-slate-900 bg-slate-50 px-3 py-2.5 md:px-4 md:py-3 ${className}`}
        >
            <div className="flex min-w-0 items-center gap-3">
                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-800">
                    <i className={`bi ${icon} text-base`} aria-hidden />
                </span>
                <div className="min-w-0">
                    <p className="mb-0 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                        Post type
                    </p>
                    <p className="mb-0 text-base font-bold leading-tight text-slate-900 md:text-lg">
                        {formatAssetLabel(resolvedAssetType)}
                    </p>
                </div>
            </div>

            {hasSideMeta ? (
                <div className="flex shrink-0 flex-col items-end gap-1 text-xs text-slate-500">
                    {createdLabel ? (
                        <span className="inline-flex items-center gap-1 whitespace-nowrap">
                            <i className="bi bi-calendar3" aria-hidden />
                            {createdLabel}
                        </span>
                    ) : null}
                    {campaignId ? (
                        <Link
                            to={buildCampaignPath(campaignId)}
                            className="inline-flex items-center gap-0.5 font-medium text-blue-600 hover:text-slate-800 hover:underline"
                        >
                            View campaign
                            <i className="bi bi-arrow-right-short text-sm" aria-hidden />
                        </Link>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}

function getMediaIcon(type) {
    if (type === "video") return "bi-play-btn";
    if (type === "image") return "bi-image";
    if (type) return getAssetIcon(type);
    return "bi-file-earmark";
}

function getMediaDisplayContent(media) {
    if (!media) return { type: null, suggestion: null, prompt: null };

    if (isNewMediaFormat(media)) {
        return {
            type: media.type ?? null,
            suggestion: media.suggestion?.trim() || null,
            prompt: media.ai_prompt?.trim() || null,
        };
    }

    const { type, description = {} } = media;
    const imageDesc = description.image?.trim() || null;
    const storyboard = description.video_storyboard?.trim() || null;
    const promptParts = [];

    if (storyboard && storyboard !== imageDesc) {
        promptParts.push(storyboard);
    }
    if (description.voiceover_or_song?.trim()) {
        promptParts.push(`Voiceover/Song: ${description.voiceover_or_song.trim()}`);
    }
    if (description.tips?.length) {
        promptParts.push(...description.tips.map((tip) => `- ${tip}`));
    }

    return {
        type: type ?? null,
        suggestion: imageDesc || storyboard || null,
        prompt: promptParts.length > 0 ? promptParts.join("\n") : null,
    };
}

export function hasRenderablePostMedia(media) {
    return getPostMediaList(media).some((item) => {
        const { suggestion, prompt } = getMediaDisplayContent(item);
        return Boolean(suggestion || prompt);
    });
}

function MediaContentTypeChip({ type }) {
    const label = getMediaTypeLabel(type) || "Media";
    const icon = getMediaIcon(type);

    return (
        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-slate-500">
            <i className={`bi ${icon} text-[10px]`} aria-hidden />
            {label}
        </span>
    );
}

function MediaSuggestionCard({ media, index, total, onCopy }) {
    const { type, suggestion, prompt } = getMediaDisplayContent(media);
    const [showSuggestion, setShowSuggestion] = useState(false);
    const typeLabel = getMediaTypeLabel(type) || "Media";
    const cardLabel = total > 1 ? `${typeLabel} #${index + 1}` : typeLabel;

    if (!suggestion && !prompt) return null;

    return (
        <article className="space-y-2">
            <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <MediaContentTypeChip type={type} />
                    {total > 1 ? (
                        <span className="text-xs font-medium text-slate-500">{cardLabel}</span>
                    ) : null}
                </div>
            </div>

            {prompt ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 md:px-4 md:py-3">
                    <div className="mb-1.5 flex items-center justify-between gap-2">
                        <p className="mb-0 text-[10px] font-bold uppercase tracking-wide text-slate-500">
                            AI prompt
                        </p>
                        {onCopy ? (
                            <CopyButton
                                label="Copy"
                                onClick={() => onCopy(prompt, "Prompt copied to clipboard")}
                            />
                        ) : null}
                    </div>
                    <p className="mb-0 whitespace-pre-wrap break-words text-sm font-normal leading-relaxed text-slate-900">{prompt}</p>
                </div>
            ) : null}

            {suggestion ? (
                <div>
                    <button
                        type="button"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 transition hover:text-slate-900"
                        aria-expanded={showSuggestion}
                        onClick={() => setShowSuggestion((open) => !open)}
                    >
                        <i
                            className={`bi ${showSuggestion ? "bi-chevron-up" : "bi-chevron-down"}`}
                            aria-hidden
                        />
                        {showSuggestion ? "Hide suggestion" : "View suggestion"}
                    </button>
                    {showSuggestion ? (
                        <div className="mt-2 rounded-lg border border-slate-200 bg-white px-3 py-2.5">
                            <div className="mb-1 flex items-center justify-between gap-2">
                                <p className="mb-0 text-[10px] font-bold uppercase tracking-wide text-slate-500">
                                    Suggestion
                                </p>
                                {onCopy ? (
                                    <CopyButton
                                        label="Copy"
                                        onClick={() =>
                                            onCopy(suggestion, "Suggestion copied to clipboard")
                                        }
                                    />
                                ) : null}
                            </div>
                            <p className="mb-0 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
                                {suggestion}
                            </p>
                        </div>
                    ) : null}
                </div>
            ) : null}
        </article>
    );
}

export function MediaSuggestion({ media, onCopy = null }) {
    const items = getPostMediaList(media).filter((item) => {
        const { suggestion, prompt } = getMediaDisplayContent(item);
        return Boolean(suggestion || prompt);
    });

    if (items.length === 0) return null;

    return (
        <div className="space-y-4">
            {items.map((item, index) => (
                <MediaSuggestionCard
                    key={index}
                    media={item}
                    index={index}
                    total={items.length}
                    onCopy={onCopy}
                />
            ))}
        </div>
    );
}

export function MediaSuggestionsTitle({ mediaList }) {
    const renderableCount = mediaList.filter((item) => {
        const { suggestion, prompt } = getMediaDisplayContent(item);
        return Boolean(suggestion || prompt);
    }).length;
    const plural = renderableCount > 1;

    return (
        <span>Media{plural ? ` (${renderableCount})` : ""}</span>
    );
}

function CopyButton({ label, onClick, disabled }) {
    return (
        <button
            type="button"
            className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900 disabled:opacity-40"
            onClick={onClick}
            disabled={disabled}
        >
            <i className="bi bi-copy" aria-hidden />
            {label}
        </button>
    );
}

function HighlightBlock({
    title,
    onCopy,
    copyLabel,
    copyDisabled = false,
    bordered = true,
    children,
}) {
    const className = bordered
        ? "min-w-0 max-w-full rounded-xl border border-slate-200 bg-white p-3 shadow-sm md:p-4"
        : "min-w-0 max-w-full";

    return (
        <div className={className}>
            <div className="mb-2 flex items-center justify-between gap-3">
                <div className="mb-0 text-sm font-semibold text-slate-900">{title}</div>
                <CopyButton label={copyLabel} onClick={onCopy} disabled={copyDisabled} />
            </div>
            {children}
        </div>
    );
}

function HashtagPills({ tags, onCopy }) {
    const hashtagsText = formatHashtagsString(tags);

    return (
        <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm md:p-4">
            <div className="mb-2 flex items-center justify-between gap-3">
                <p className="mb-0 text-sm font-semibold text-slate-900">Hashtags</p>
                <CopyButton
                    label="Copy all"
                    onClick={() => onCopy(hashtagsText, "Hashtags copied to clipboard")}
                />
            </div>
            <div className="flex flex-wrap gap-2">
                {tags.map((tag, tagIdx) => {
                    const formatted = formatHashtag(tag);
                    if (!formatted) return null;

                    return (
                        <span
                            key={`${tag}-${tagIdx}`}
                            className="inline-flex items-center gap-0.5 rounded-full border border-slate-200 bg-slate-50 py-1 pl-3 pr-1 text-xs font-medium text-slate-700 md:text-sm"
                        >
                            {formatted}
                            <button
                                type="button"
                                className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-slate-500 hover:bg-slate-200/80 hover:text-slate-900"
                                onClick={() => onCopy(formatted, "Hashtag copied to clipboard")}
                                aria-label={`Copy ${formatted}`}
                            >
                                <i className="bi bi-copy text-xs" aria-hidden />
                            </button>
                        </span>
                    );
                })}
            </div>
        </div>
    );
}

function formatHashtagsString(tags = []) {
    return tags.map(formatHashtag).filter(Boolean).join(" ");
}

function getPostTags(post) {
    return post?.hash_tags ?? post?.hashTags ?? [];
}

export const DEFAULT_POST_ASSET_TYPE = "post";

export function getPostAssetType(post) {
    return post?.asset_type ?? post?.assetType ?? DEFAULT_POST_ASSET_TYPE;
}

export default function PostCard({
    post,
    onCopy,
    createdLabel = null,
    campaignId = null,
    meta = null,
}) {
    const tags = getPostTags(post);
    const hashtagsText = formatHashtagsString(tags);
    const assetType = getPostAssetType(post);
    const mediaList = getPostMediaList(post.media);
    const mediaCopyText = formatMediaCopyText(post.media);
    const resolvedCampaignId = campaignId ?? null;

    return (
        <div className="min-w-0 w-full max-w-full space-y-3">
            {meta ? <div className="w-full text-sm text-slate-500">{meta}</div> : null}

            <PostTypeHeader
                assetType={assetType}
                createdLabel={createdLabel}
                campaignId={resolvedCampaignId}
            />

            <HighlightBlock
                title="Caption"
                copyLabel="Copy caption"
                copyDisabled={!post.caption}
                onCopy={() => onCopy(post.caption, "Caption copied to clipboard")}
            >
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 md:px-4 md:py-3">
                    <p className="mb-0 whitespace-pre-wrap break-words text-sm font-normal leading-relaxed text-slate-900">
                        {post.caption || "—"}
                    </p>
                </div>
            </HighlightBlock>

            {hashtagsText ? <HashtagPills tags={tags} onCopy={onCopy} /> : null}

            {hasRenderablePostMedia(post.media) ? (
                <HighlightBlock
                    title={<MediaSuggestionsTitle mediaList={mediaList} />}
                    copyLabel="Copy media"
                    copyDisabled={!mediaCopyText}
                    onCopy={() => onCopy(mediaCopyText, "Media copied to clipboard")}
                >
                    <MediaSuggestion media={post.media} onCopy={onCopy} />
                </HighlightBlock>
            ) : null}
        </div>
    );
}

export function PostCardMeta({ createdLabel, campaignId }) {
    return (
        <PostTypeHeader
            createdLabel={createdLabel}
            campaignId={campaignId}
        />
    );
}
